import { QueryClient, queryOptions } from '@tanstack/react-query';

import { getQueryFn } from '../utils';
import { Event } from './types';

const FAR_FUTURE_DATE = Date.parse('2100-01-01T00:00:00Z');

export const getEventsQueryOptions = (params: {
  startTime: number;
  endTime?: number;
  habitatIds?: string[];
  animals?: string[];
  zoos?: string[];
  /** Filter to a single zoo by id (used by the partner "My Events" filter). */
  zooId?: string;
  /** Scope to events created by the current user (used by the admin "My Events" filter). */
  mine?: boolean;
  events?: string[];
  tags?: string[];
  sort?: 'desc' | 'asc';
  pageSize?: number;
}) => {
  const habitatId = params.habitatIds?.length ? params.habitatIds.join(',') : undefined;
  const queryParams = { pageSize: 99, endTime: FAR_FUTURE_DATE, sort: 'asc', ...params, habitatIds: undefined, habitatId };
  return queryOptions({
    queryKey: ['schedules', 'list', queryParams],
    queryFn: getQueryFn<Event[]>('/schedules/events', queryParams),
  });
};

export const getEventQueryOptions = (scheduleId: string | null | undefined) =>
  queryOptions({
    queryKey: ['schedules', 'details', scheduleId],
    queryFn: getQueryFn<Event>(`/schedules/events/${scheduleId}`),
    enabled: !!scheduleId,
  });

const withOneMoreInterested = (event: Event) => ({ ...event, interestedCount: (event.interestedCount ?? 0) + 1 });

/**
 * Count the viewer's own reminder into every cached copy of an event, so the card they just acted on
 * — and any other card showing the same event — reflects it immediately. A patch rather than an
 * invalidation: the server's own number arrives on the next fetch once the data goes stale.
 */
export const addInterestedToCachedEvent = (queryClient: QueryClient, eventId: string) => {
  queryClient.setQueriesData<Event[]>(
    { queryKey: ['schedules', 'list'] },
    (events) => events?.map((event) => (event._id === eventId ? withOneMoreInterested(event) : event)),
  );
  queryClient.setQueriesData<Event>({ queryKey: ['schedules', 'details'] }, (event) =>
    event?._id === eventId ? withOneMoreInterested(event) : event,
  );
};
